import type { EnvironmentConfig } from '#shared/environment.config.dto';
import { defu } from 'defu';

// Only the blocks the client actually consumes cross into the payload: the full environment
// YAML also carries server-only blocks (pino logstash host and port, csp) that no client code
// reads — the payload is world-readable in the served HTML.
type ClientEnvironmentConfig = Pick<EnvironmentConfig, 'app' | 'api' | 'krypton' | 'beacon'>;

export default defineNuxtPlugin({
  name: 'runtime-config',
  setup(nuxtApp) {
    const appConfig = useAppConfig(); // Static config from app.config.ts
    // The exact config this request was rendered with (set by server/plugins/05.app-config-ssr),
    // serialized into the payload so the client hydrates with the very same values.
    // Re-fetching /api/config instead would run the config lookup a second time: any failure or
    // divergence of that second call desynchronizes hydration for the whole app.
    const requestConfig: EnvironmentConfig | undefined = useRequestEvent()?.context.appConfig;
    const environmentConfig = useState<ClientEnvironmentConfig>('app-config', () => {
      if (!requestConfig) {
        return {};
      }
      const { app, api, krypton, beacon } = requestConfig;
      return { app, api, krypton, beacon };
    });

    // Merge static + dynamic, deeply: the server sends only what it computes, a spread would drop the rest of a block
    const mergedConfig = defu(environmentConfig.value, appConfig);

    // While the server renders, the BFF is reached inside the network; the browser keeps the path.
    if (requestConfig?.internalApi?.url && mergedConfig.api) {
      mergedConfig.api = { ...mergedConfig.api, url: requestConfig.internalApi.url };
    }

    // Provide merged config globally
    nuxtApp.provide('appConfig', mergedConfig);
  },
});
